import { useMemo } from 'react';

import { SMALL_JOB_AREA } from '@common/constants';
import { useShapeArea } from '@common/hooks/useShapeArea/useShapeArea';
import { ProductDetails } from '@common/models';
import { ProductFilters, doesProductMatchFilter } from '@common/utils/productFilterUtility';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useProductDetailsContext } from '@ee-context';
import { useAppSelector } from '@redux/hooks';
import { selectValidSmallTicketNumber } from '@redux/slices/estimateSlice';

export interface UseSmallJobsReturn {
    /**
     * This is the value to be used to both test for "is a small job" and also the minimum area
     * that will be charged for small jobs unless there's an override.
     */
    smallJobMinimumArea: number;

    /**
     * This product for the fee that will be charge for fee-based small jobs.
     * If this is non-null, then colors tagged for small job use will add this to the project if the area is within range
     * It is the first product in the catalog that matches the filter ProductFilters.smallProjectFee
     */
    smallJobFeeProduct: ProductDetails | undefined;

    /**
     * Does the estimate qualify as a small job? This is the broad definition, it may also be a smallJobWithFee,
     * which takes priority.
     */
    isSmallJob: boolean;

    /**
     * Does this estimate qualify as a small job, with isSmallJobColor and smallJobFeeProduct exists
     */
    isSmallJobWithFee: boolean; // true if it's a small job and there's a fee product available

    /**
     * Is the currently selected color eligible for a small-project fee?
     */
    isSmallJobColor: boolean;

    /**
     * Should we use the actual area for pricing?
     * True if it's a small-job-with-fee OR if it's a small job and has an override code
     * This is the inverse of useMinQtyAreaForPricing
     */

    useActualAreaForPricing: boolean;

    /**
     * Should we use the defined minimum quantity area for pricing?
     * This is the inverse of useActualAreaForPricing
     */
    useMinQtyAreaForPricing: boolean;

    /**
     * Should the small job minimum quantity override be shown?
     */
    canOverrideSmallJob: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useSmallJobs = (): UseSmallJobsReturn => {
    const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);
    const { selectedValue: selectedColor } = useSelectedColor();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { totalMaterialArea } = useShapeArea();
    const { productCatalog } = useProductDetailsContext();
    const { retailerSettings } = useRetailerSettings();

    /**
     * This product is the fee that will be charge for small jobs (>10 && <25) IF it's a smallJobColor
     * It is the first product in the catalog that matches the filter ProductFilters.smallProjectFee
     */
    const smallJobFeeProduct = useMemo(() => {
        return Array.from(productCatalog.values()).find((product) =>
            doesProductMatchFilter(ProductFilters.smallProjectFee, product)
        );
    }, [productCatalog]);

    /**
     * If the selectedColor is identified to be a small job color, then it can be used in small jobs
     * but the smallJobFeeProduct will be added to the estimate.
     */
    const isSmallJobColor = useMemo(
        () => (selectedColor ? doesProductMatchFilter(ProductFilters.smallProjectColor, selectedColor) : false),
        [selectedColor]
    );

    /**
     * this is the maximum area allowed to qualify as a small job
     */
    const smallJobThreshold = selectedMaterial?.installedMinSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH;

    /**
     * Calculations to test if the small job fee costs more than the minimium sqft
     */
    // const actualAreaPlusFeeCost = totalMaterialArea * (selectedColor?.price ?? 0) + (smallJobFeeProduct?.price ?? 0);
    // const thresholdAreaCost = smallJobThreshold * (selectedColor?.price ?? 0);

    /**
     * Does this qualify as a generic "small job"
     */
    const isSmallJob = totalMaterialArea > 0 && totalMaterialArea < smallJobThreshold;

    /**
     * Is this s small job color AND there's an available small job fee?
     */
    const isSmallJobFeeEligible = totalMaterialArea >= SMALL_JOB_AREA.LOW && !!smallJobFeeProduct && isSmallJobColor;

    /**
     * this is the minimum area allowed before a job qualifies as a small job
     */
    const smallJobMinimumArea = selectedMaterial?.installedMinSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH;

    /**
     * If it's a smallJob by area, smallJobColor, a smallJobFeeProduct is available
     * then this will get a small job fee product added to the estimate, and we should use the actual area for pricing
     */
    const isSmallJobWithFee = isSmallJob && isSmallJobFeeEligible;

    /**
     * Calculations to test if the small job fee costs more than the minimium sqft
     */

    /**
     * Use actual area if
     * * it's not a small job, OR
     * * it's a small job with fee
     * * we have a valid ticket number
     */
    const useActualAreaForPricing = !isSmallJob || isSmallJobWithFee || !!validatedTicketNumber;

    const canOverrideSmallJob = useMemo(() => {
        return !!retailerSettings?.confirmSmallProjectOverride && isSmallJob && !isSmallJobWithFee;
    }, [isSmallJob, isSmallJobWithFee, retailerSettings?.confirmSmallProjectOverride]);

    return {
        smallJobMinimumArea,
        smallJobFeeProduct,
        useMinQtyAreaForPricing: !useActualAreaForPricing,
        isSmallJob,
        isSmallJobColor,
        useActualAreaForPricing,
        isSmallJobWithFee,
        canOverrideSmallJob,
    };
};
